/**
 * Packing queue URL state. A packer is handed a range ("twenty orders, starting at fifty"), so
 * every control lives in the query string: the page survives a reload and the range can be sent
 * to the person picking it.
 *
 * Not a `.tsx`: a non-component export there costs the page its Fast Refresh state.
 */
import {
  PACKING_QUEUE_SORTS,
  type PackingQueueSort,
} from "@loot-os/fulfillment-core/order/packing-fold";

/** A timestamp as it arrives from tRPC — an ISO string, since this router has no transformer. */
export type Dateish = string | Date;

export function toDate(value: Dateish): Date {
  return typeof value === "string" ? new Date(value) : value;
}

/** The three tabs, in Retool's order. */
export const PACKING_TABS = [
  { value: "unpacked", label: "Unpacked Orders" },
  { value: "partial", label: "Partial Packed" },
  { value: "missing", label: "Missing Cards" },
] as const;

/** Retool's three tabs. "missing" is a different report, not a filter over bags. */
export type PackingTab = "unpacked" | "partial" | "missing";

/** Which pick-sheet rows to show: everything, only pickable, or only the problems. */
type StockFilter = "in" | "short";

export interface PackingSearch {
  q?: string;
  sort?: PackingQueueSort;
  dir?: "asc" | "desc";
  offset?: number;
  /** Rows fetched and shown per page. */
  limit?: number;
  /**
   * How many orders the batch checkbox takes. Separate from `limit` on purpose: a packer's
   * instruction ("twenty orders, starting at fifty") sizes the batch, not the page, and the
   * two were one control until they collided.
   */
  batch?: number;
  /** Selected order ids, comma-joined, so a sheet is a link. */
  sel?: string;
  /** "sheet" swaps the table for the combined pick view. */
  view?: "sheet";
  /** Narrows the pick sheet to what can be picked now, or to what cannot. */
  stock?: StockFilter;
  /** One of `attributes.details.category`: POKEMON, MTG, LORCANA … */
  cat?: string;
  /** Retool's toggle, off by default: hide bags the shelf cannot cover. */
  oos?: boolean;
  /** Which tab is open. */
  tab?: PackingTab;
  /** Created-at range, as ISO strings from the datetime inputs. */
  from?: string;
  to?: string;
  /**
   * Retool's SLA input, in weeks, defaulting to 0. 0 means every waiting bag reserves the stock
   * it needs even when it cannot be filled; raising it narrows that to bags past the threshold.
   */
  sla?: number;
}

function text(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function counting(value: unknown): number | undefined {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : undefined;
}

function isSort(value: unknown): value is PackingQueueSort {
  return PACKING_QUEUE_SORTS.includes(value as PackingQueueSort);
}

export function parsePackingSearch(search: Record<string, unknown>): PackingSearch {
  return {
    q: text(search.q),
    sort: isSort(search.sort) ? search.sort : undefined,
    dir: search.dir === "desc" ? "desc" : search.dir === "asc" ? "asc" : undefined,
    offset: counting(search.offset),
    limit: counting(search.limit),
    batch: counting(search.batch),
    sel: text(search.sel),
    view: search.view === "sheet" ? "sheet" : undefined,
    stock: search.stock === "in" || search.stock === "short" ? search.stock : undefined,
    cat: text(search.cat),
    oos: search.oos === true || search.oos === "true" ? true : undefined,
    tab:
      search.tab === "partial" || search.tab === "missing" || search.tab === "unpacked"
        ? search.tab
        : undefined,
    from: text(search.from),
    to: text(search.to),
    sla: counting(search.sla),
  };
}

/** The selection as ids. Empty when nothing is selected. */
export function selectedIds(sel: string | undefined): string[] {
  return sel ? sel.split(",").filter(Boolean) : [];
}

export function toSel(ids: string[]): string | undefined {
  return ids.length > 0 ? ids.join(",") : undefined;
}

/** "51–70 of 412", or "0 of 0" when the filter matches nothing. */
export function rangeLabel(offset: number, shown: number, total: number): string {
  if (total === 0 || shown === 0) return `0 of ${total}`;
  return `${offset + 1}–${offset + shown} of ${total}`;
}

/** How long a bag has been waiting, which is what decides whether it is late. */
export function ageInDays(createdAt: Dateish, now: Date = new Date()): number {
  return Math.floor((now.getTime() - toDate(createdAt).getTime()) / 86_400_000);
}
